import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tablesApi } from '../../api/endpoints';
import { useAuthStore } from '../../stores/auth.store';
import Modal from '../../components/common/Modal';

interface TableData {
  id: string;
  name: string;
  status: 'available' | 'occupied' | 'reserved';
  items?: unknown[];
  customer?: string;
}

export default function TablesPage() {
  const { t } = useTranslation();
  const shop = useAuthStore((s) => s.shop);
  const queryClient = useQueryClient();

  const { data: res, isLoading } = useQuery({
    queryKey: ['tables', shop?.id],
    queryFn: () => tablesApi.read(shop!.id),
    enabled: !!shop?.id,
  });

  const updateTables = useMutation({
    mutationFn: (data: unknown) => tablesApi.update(shop!.id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tables'] }),
  });

  const tables: TableData[] = Array.isArray(res?.data) ? (res.data as TableData[]) : [];

  const [selectedTable, setSelectedTable] = useState<TableData | null>(null);

  function getStatusColor(status: string) {
    switch (status) {
      case 'available': return '#16a34a';
      case 'occupied': return '#dc2626';
      case 'reserved': return '#d97706';
      default: return '#6b7280';
    }
  }

  async function toggleStatus(table: TableData) {
    const next = table.status === 'available' ? 'occupied' : 'available';
    const updated = tables.map((t) =>
      t.id === table.id ? { ...t, status: next } : t,
    );
    await updateTables.mutateAsync(updated);
  }

  if (isLoading) {
    return <div className="page"><p>Loading tables...</p></div>;
  }

  if (tables.length === 0) {
    return (
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Tables</h1>
        </div>
        <div className="empty-state">
          <p>No tables configured.</p>
          <p className="text-muted">Tables can be configured via the table management API.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Tables</h1>
      </div>

      <div className="tables-grid">
        {tables.map((table) => (
          <button
            key={table.id}
            className="table-card"
            style={{ borderColor: getStatusColor(table.status) }}
            onClick={() => setSelectedTable(table)}
          >
            <div
              className="table-card__status-dot"
              style={{ backgroundColor: getStatusColor(table.status) }}
            />
            <div className="table-card__name">{table.name}</div>
            <div className="table-card__status">{table.status}</div>
            {table.items && table.items.length > 0 && (
              <div className="table-card__items">{table.items.length} items</div>
            )}
          </button>
        ))}
      </div>

      {/* Table detail modal */}
      <Modal
        open={!!selectedTable}
        onClose={() => setSelectedTable(null)}
        title={selectedTable ? `Table: ${selectedTable.name}` : ''}
        size="md"
      >
        {selectedTable && (
          <div className="table-detail">
            <div className="table-detail__status">
              Status:
              <span
                className="badge"
                style={{ color: getStatusColor(selectedTable.status) }}
              >
                {selectedTable.status}
              </span>
            </div>

            {selectedTable.customer && (
              <p>Customer: {selectedTable.customer}</p>
            )}

            {selectedTable.items && selectedTable.items.length > 0 ? (
              <div>
                <h4>Items</h4>
                <ul>
                  {selectedTable.items.map((item, i) => (
                    <li key={i}>{JSON.stringify(item)}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-muted">No items on this table.</p>
            )}

            <div className="form-actions">
              <button
                className="btn btn--outline"
                onClick={() => {
                  toggleStatus(selectedTable);
                  setSelectedTable(null);
                }}
              >
                Mark as {selectedTable.status === 'available' ? 'Occupied' : 'Available'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
